/**
 * Client mirror of the server's validation of the org-profile PUT body
 * (`PartnerOrgProfileInput` in `server/app/models/api/partner.py`).
 *
 * The server re-validates and is the authority, answering 422 with a specific
 * `detail`. These checks gate the submit button and show inline errors so the
 * user doesn't round-trip to a 422.
 *
 * The NPI is optional for Provider and Pharmacy orgs, and a present one is
 * validated by the shared `validateNpi`; a Broker has none and validates its
 * NPN and state licenses instead. DEA is optional and unvalidated here; the
 * server only trims it.
 */
import type { ApiError, StateLicense } from "@dearbill/shared";

// Broker credentials, mirroring the server's `_NPN_RE` and `_LICENSE_RE`;
// license numbers are uppercased on save.
const NPN_RE = /^[1-9]\d{0,9}$/;
const LICENSE_RE = /^[A-Za-z0-9-]{1,20}$/;
const TAX_ID_RE = /^\d{2}-\d{7}$/;

/**
 * Validates the Broker NPN: required and matching `NPN_RE`. Returns the error
 * message, or `null` when acceptable.
 */
export function validateNpn(npn: string): string | null {
  const trimmed = npn.trim();
  if (!trimmed) return "NPN is required.";
  if (!NPN_RE.test(trimmed)) {
    return "Enter a valid NPN — digits only, no leading zero, up to 10 digits.";
  }
  return null;
}

/**
 * Validates the Broker licensed-states rows: at least one row, unique states,
 * and per row a selected state and a license number matching `LICENSE_RE`.
 * Returns a list-level message and index-aligned per-row messages, all `null`
 * when valid.
 */
export function validateStateLicenses(rows: StateLicense[]): {
  list: string | null;
  rows: (string | null)[];
} {
  const states = rows.map((r) => r.state).filter(Boolean);
  const list =
    rows.length === 0
      ? "Add at least one licensed state."
      : new Set(states).size !== states.length
        ? "Each state can be listed only once."
        : null;
  const perRow = rows.map((r) =>
    !r.state
      ? "Select a state."
      : !LICENSE_RE.test(r.licenseNumber.trim())
        ? "Enter a license number of 1–20 letters, digits, or hyphens."
        : null,
  );
  return { list, rows: perRow };
}

/**
 * Validates the combined tax ID against the server rule on
 * `PartnerOrgProfileInput.taxId`: optional, so `""` passes, else `XX-XXXXXXX`.
 *
 * The form holds the two halves in digit-only boxes and combines them as
 * `prefix-suffix` (`""` when both are empty), so the only failure is missing
 * digits in a partly filled ID. Returns the error message, or `null` when
 * acceptable.
 */
export function validateTaxId(taxId: string): string | null {
  if (taxId !== "" && !TAX_ID_RE.test(taxId)) {
    return "Enter a 9-digit tax ID (e.g. 12-3456789).";
  }
  return null;
}

/**
 * Validates the org legal business name against the server rule on
 * `PartnerOrgProfileInput.legalName` (required, non-empty). Returns the
 * required-field message when blank, or `null` when acceptable.
 */
export function validateLegalName(legalName: string): string | null {
  if (!legalName.trim()) {
    return "Legal business name is required.";
  }
  return null;
}

/**
 * Friendly labels for the org-profile wire field keys (camelCase, mirroring the
 * PUT body). Nested address fields surface as their leaf key (e.g. `zip`), which
 * doesn't collide with any top-level key. Unmapped keys are humanized verbatim.
 */
const FIELD_LABELS: Record<string, string> = {
  legalName: "Legal business name",
  street1: "Street address",
  street2: "Apt, suite, etc.",
  city: "City",
  state: "State",
  zip: "ZIP",
  organizationType: "Organization type",
  entityType: "Entity type",
  npi: "NPI",
  dea: "DEA",
  taxId: "Tax ID",
  npn: "NPN",
  lineOfAuthority: "Line of authority",
  stateLicenses: "Licensed states",
  licenseNumber: "License number",
  termsAccepted: "Terms & Conditions",
};

/** Humanize a camelCase wire key as a last-resort label, so "fooBar" becomes "Foo bar". */
function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Derive a field label from a FastAPI `loc` tuple.
 *
 * `loc` looks like `["body", "zip"]` or `["body", "address", "zip"]`. The last
 * string element, skipping the leading `"body"` and any numeric list indices,
 * is the wire key to map. Returns `null` when no string key is present.
 */
function labelFromLoc(loc: unknown): string | null {
  if (!Array.isArray(loc)) return null;
  let key: string | null = null;
  for (const part of loc) {
    if (typeof part === "string" && part !== "body") key = part;
  }
  if (!key) return null;
  return FIELD_LABELS[key] ?? humanizeKey(key);
}

/**
 * Render a FastAPI error body as a readable string for the save banner.
 *
 * A 422 `detail` is a list of `{ type, loc, msg }` objects, so `ApiError.detail`
 * (built with `String(...)` in `apiFetch`) reads `"[object Object]"`; the raw
 * parsed body on `ApiError.body` holds the messages. Each message is prefixed
 * with a field label derived from its `loc`, so the user can tell which field
 * is wrong (e.g. `"ZIP: ..."`). Other errors carry a plain string `detail`.
 * Falls back to the given message.
 */
export function formatApiErrorDetail(err: ApiError, fallback: string): string {
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail: unknown = body.detail;
    if (typeof detail === "string" && detail) return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => {
          if (!d || typeof d !== "object" || !("msg" in d)) return "";
          const msg = String((d as { msg: unknown }).msg);
          if (!msg) return "";
          const label = labelFromLoc((d as { loc?: unknown }).loc);
          return label ? `${label}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (msgs.length > 0) return msgs.join("; ");
    }
  }
  return fallback;
}
